import {
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { format } from "date-fns";
import { MoreVertical } from "lucide-react";
import { Fragment } from "react/jsx-runtime";
import { useState } from "react";
import ConfirmationModal from "@/modules/common/components/modals/cofirmation-modal";
import {
  OWNER_ID_TYPE_LABELS,
  OwnerId,
} from "@/modules/property-tracks/types";
import useOwnerIds from "@/modules/property-tracks/hooks/owner-ids/use-owner-ids.hook";
import useDownloadOwnerId from "@/modules/property-tracks/hooks/owner-ids/download-owner-id.hook";
import useDeleteOwnerId from "@/modules/property-tracks/hooks/owner-ids/delete-owner-id.hook";
import UploadOwnerId from "./upload-owner-id";

const OwnerIdRecord = ({
  ownerId,
  customerUid,
}: {
  ownerId: OwnerId;
  customerUid?: string;
}) => {
  const theme = useTheme();
  const { download, isDownloading } = useDownloadOwnerId();
  const { deleteOwnerId, isDeleting } = useDeleteOwnerId(customerUid);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDownload = () => {
    download(ownerId.uid);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = () => {
    deleteOwnerId(ownerId.uid);
    setShowDeleteModal(false);
  };

  return (
    <Stack
      gap={theme.nawy.spacing.xs}
      borderRadius={theme.nawy.radius.md}
      border={`1px solid ${theme.nawy.colors.primitives.navy[100]}`}
      padding={theme.nawy.spacing.sm}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography
          sx={{ ...theme.nawy.typography.english.variants.body.md.semibold }}
        >
          {OWNER_ID_TYPE_LABELS[ownerId.type] ?? ownerId.type}
        </Typography>
        <IconButton
          onClick={handleMenuOpen}
          disabled={isDownloading || isDeleting}
        >
          <MoreVertical
            size={16}
            color={theme.nawy.colors.primitives.blue[500]}
          />
        </IconButton>
      </Stack>

      <Stack>
        {ownerId.images.map((image) => (
          <Typography
            key={image.uid}
            sx={{ fontSize: "12px", fontWeight: 600 }}
          >
            {image.fileName}
          </Typography>
        ))}
        <Typography
          variant="caption"
          color={theme.nawy.colors.semantic.text.secondary}
          sx={{
            ...theme.nawy.typography.english.variants.body.xs.regular,
          }}
        >
          {format(new Date(ownerId.createdAt), "dd MMMM yyyy")}
        </Typography>
      </Stack>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: theme.nawy.radius.lg,
              minWidth: 140,
            },
          },
        }}
      >
        <MenuItem onClick={handleDownload}>Download</MenuItem>
        <MenuItem
          onClick={handleDeleteClick}
          sx={{ color: theme.palette.error.main }}
        >
          Delete
        </MenuItem>
      </Menu>

      <ConfirmationModal
        open={showDeleteModal}
        setOpen={setShowDeleteModal}
        onConfirmHandler={handleDeleteConfirm}
        onCancelHandler={() => setShowDeleteModal(false)}
        title="Delete Owner ID"
        body="Are you sure you want to delete this owner ID? This action cannot be undone."
        isLoading={isDeleting}
      />
    </Stack>
  );
};

const OwnerIdCard = ({ customerUid }: { customerUid?: string }) => {
  const theme = useTheme();
  const { data: ownerIds } = useOwnerIds({ customerUid });

  const hasOwnerIds = !!ownerIds && ownerIds.length > 0;

  return (
    <Stack
      bgcolor={theme.nawy.colors.primitives.white[100]}
      gap={theme.nawy.spacing.sm}
      borderRadius={theme.nawy.radius.lg}
      padding={theme.nawy.spacing.md}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography
          sx={{ ...theme.nawy.typography.english.variants.body.md.semibold }}
        >
          Owner ID
        </Typography>

        {hasOwnerIds && <UploadOwnerId customerUid={customerUid} />}
      </Stack>

      {hasOwnerIds ? (
        <Fragment>
          <Stack gap={theme.nawy.spacing.sm}>
            {ownerIds.map((ownerId) => (
              <OwnerIdRecord
                key={ownerId.uid}
                ownerId={ownerId}
                customerUid={customerUid}
              />
            ))}
          </Stack>
        </Fragment>
      ) : (
        <>
          <Typography
            variant="caption"
            color={theme.nawy.colors.semantic.text.secondary}
            sx={{
              ...theme.nawy.typography.english.variants.body.md.regular,
            }}
          >
            {customerUid
              ? "The property has no owner id yet."
              : "No current owner is set for this property."}
          </Typography>
          <UploadOwnerId customerUid={customerUid} showLabel />
        </>
      )}
    </Stack>
  );
};

export default OwnerIdCard;
